// Tickets Overview — mirrors the sibling HSE product's Overview screen
// (map of sites with active reports + an analytics rail), adapted to
// this app's actual Requests data model. Two differences from that
// screen worth calling out:
//   • HSE requests carry a fixed `risk_category` enum + a per-user
//     acknowledgement system; this app's Requests have neither. Status
//     here is 'submitted' | 'approved' | 'declined' | 'closed', so the
//     filter chip and stat strip use that instead of ack state, and
//     there's no "Acknowledge" action on the map card.
//   • The category-breakdown panel ("Reports by risk category" there)
//     is "Requests by Issue Template" here: pick a template, see the
//     breakdown of its items among matching requests — the closest
//     real equivalent, since Issue Templates are the named containers
//     of picker labels reporters choose from on the public QR form
//     (see api/src/modules/issue-templates/issue-templates.service.js).
//   • Added: a "with Work Order" stat + a per-request indicator,
//     since converting a request into a Work Order is core to this
//     app's workflow (api.requests.approve / linkWorkOrder).

import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box, Card, Chip, CircularProgress, Divider, IconButton, Stack,
  ToggleButton, ToggleButtonGroup, Typography, useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { LineChart } from '@mui/x-charts/LineChart'

import { useQuery } from '@tanstack/react-query'
import type { Site, IssueTemplate } from '@cmms/types'
import type { MaintenanceRequest } from '@cmms/api-client'

import { api } from '../../lib/api'
import { useOrgSettings } from '../../hooks/useOrgSettings'
import { SearchableSelect } from '../../components/SearchableSelect'

type Filter = 'all' | 'open' | 'closed'

// Synthetic entry merged into the site picker — id '' matches the "no
// filter" state everything else in this tab already treats as "show
// all requests" (map, stats, rest of the rail). Same convention as
// ALL_TEMPLATES_OPTION below, just a lighter shape since the site
// picker only ever needs id + name.
const ALL_SITES_OPTION = { id: '', name: 'All sites' }

function isOpen(r: MaintenanceRequest): boolean {
  return r.status === 'submitted' || r.status === 'approved'
}

export function TicketsOverview() {
  const [filter, setFilter] = useState<Filter>('all')
  // '' = All templates. Lifted up here (not local to
  // RequestsByIssueTemplate) so picking a template scopes the whole
  // tab — map, stats, and the rest of the rail — not just that one panel.
  const [templateId, setTemplateId] = useState('')
  // '' = All sites. Same "scope the whole tab" treatment as templateId.
  const [siteId, setSiteId] = useState('')

  const sitesQ     = useQuery({ queryKey: ['sites'],    queryFn: () => api.sites.list() })
  const requestsQ  = useQuery({ queryKey: ['requests'], queryFn: () => api.requests.list() })
  const templatesQ = useQuery({ queryKey: ['issue-templates'], queryFn: () => api.issueTemplates.list(), staleTime: 60_000 })

  const sites: Site[] = Array.isArray(sitesQ.data) ? sitesQ.data : []
  const templates: IssueTemplate[] = Array.isArray(templatesQ.data) ? templatesQ.data : []
  const allRequests: MaintenanceRequest[] = Array.isArray(requestsQ.data) ? requestsQ.data : []
  const requests = useMemo(
    () => allRequests
      .filter((r) => !templateId || r.issue_template_id === templateId)
      .filter((r) => !siteId || r.site_id === siteId),
    [allRequests, templateId, siteId],
  )

  const siteOptions = useMemo(
    () => [ALL_SITES_OPTION, ...sites.map((s) => ({ id: s.id, name: s.name }))],
    [sites],
  )

  // Group visible requests by site id — driven by the filter chip so
  // markers appear/disappear as you toggle.
  const visibleBySite = useMemo(() => {
    const bySite = new Map<string, MaintenanceRequest[]>()
    for (const r of requests) {
      if (!r.site_id) continue
      if (filter === 'open' && !isOpen(r)) continue
      if (filter === 'closed' && isOpen(r)) continue
      const arr = bySite.get(r.site_id) ?? []
      arr.push(r)
      bySite.set(r.site_id, arr)
    }
    return bySite
  }, [requests, filter])

  const pinSites: PinSite[] = useMemo(() => {
    const out: PinSite[] = []
    for (const s of sites) {
      const rs = visibleBySite.get(s.id)
      if (!rs || rs.length === 0) continue
      if (s.latitude == null || s.longitude == null) continue
      out.push({ site: s, requests: rs })
    }
    return out
  }, [sites, visibleBySite])

  const stats = useMemo(() => {
    const open = requests.filter(isOpen).length
    // Work orders and tasks share one table (kind), so "has a linked
    // work_order_id" doesn't say which one it became — split them.
    const workOrders = requests.filter((r) => r.work_order_kind === 'work_order').length
    const tasks      = requests.filter((r) => r.work_order_kind === 'task').length
    return {
      sites: sites.length,
      // Same population as the map's own pin count (pinSites), not the
      // broader visibleBySite — a site with matching requests but no
      // lat/long isn't mappable, and this tile sits right above a map
      // whose own pin-count chip only counts mappable sites. Using a
      // different, larger population here made the two numbers
      // disagree while both claimed to mean "sites with activity."
      active: pinSites.length,
      requests: requests.length,
      open,
      workOrders,
      tasks,
    }
  }, [sites, requests, pinSites])

  const loading = sitesQ.isLoading || requestsQ.isLoading

  return (
    <Stack spacing={2} sx={{ minHeight: 0 }}>
      {/* ── Site filter — scopes the whole tab (map, stats, and every
          panel in the rail), same "lift the state up" treatment as
          the issue-template filter below. ── */}
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'text.secondary', flexShrink: 0 }}>
          Site
        </Typography>
        <Box sx={{ width: 280 }}>
          <SearchableSelect
            value={siteId}
            onChange={setSiteId}
            options={siteOptions}
            getValue={(s) => s.id}
            getLabel={(s) => s.name}
            placeholder="Select a site"
            loading={sitesQ.isLoading}
            size="small"
          />
        </Box>
      </Stack>

      {/* ── Compact stat strip ─────────────────────────────────── */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
        <StatTile label="Sites"            icon={LocationOnOutlinedIcon}         value={stats.sites} />
        <StatTile label="Active sites"     icon={LocationOnOutlinedIcon}         value={stats.active} tone="brand" />
        <StatTile label="Requests (total)" icon={DescriptionOutlinedIcon}        value={stats.requests} />
        <StatTile label="Open"             icon={ScheduleOutlinedIcon}           value={stats.open} tone="warn" />
        <StatTile label="→ Work orders"    icon={BuildOutlinedIcon}              value={stats.workOrders} tone="ok" />
        <StatTile label="→ Tasks"          icon={AssignmentTurnedInOutlinedIcon} value={stats.tasks} tone="brand" />
      </Stack>

      {/* ── Body — map on the left, analytics rail stacked on the right. */}
      <Box sx={{
        flex: 1, minHeight: 500,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 340px' },
        gap: 2,
      }}>
        <Card sx={{ minHeight: 0, borderRadius: 3, overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
          <Stack direction="row" alignItems="center" spacing={1.5}
            sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Sites with active requests</Typography>
            <Chip label={pinSites.length} size="small"
              sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: 'action.hover' }} />
            <Box sx={{ flex: 1 }} />
            <ToggleButtonGroup value={filter} exclusive size="small"
              onChange={(_, v: Filter | null) => v && setFilter(v)}
              sx={{ '& .MuiToggleButton-root': { fontSize: 12, textTransform: 'none', px: 1.5, py: 0.5, borderRadius: 1.5 } }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="open">Open</ToggleButton>
              {/* isOpen() below only matches 'submitted'/'approved', so
                  this bucket is really "everything else" — 'closed' AND
                  'declined' — labelled to say so instead of implying
                  it's just closed requests. */}
              <ToggleButton value="closed">Closed / Declined</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Box sx={{ flex: 1, minHeight: 400, position: 'relative' }}>
            {loading ? (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={26} />
              </Box>
            ) : (
              <SiteRequestsMap pinSites={pinSites} />
            )}
          </Box>
        </Card>

        <Stack direction={{ xs: 'column', md: 'row', lg: 'column' }} spacing={2}
          sx={{ minHeight: 0, overflowY: { lg: 'auto' }, pr: { lg: 0.5 } }}>
          {/* `lg: 'none'` on every panel (not a fixed px height) — the
              header + inner maxHeight-bounded scroll area inside each
              card already determine its total content height, and each
              panel keeps that natural size instead of being squeezed.
              TopSites used to be `lg: '1 1 0'` (grow to fill leftover
              space) — but at `lg` this Stack is a CSS Grid item, so
              `flex` doesn't apply to it at all; its actual size comes
              from the grid row (stretched to match the map's height).
              With 3 non-shrinking siblings often already consuming that
              whole row, TopSites' "leftover space" was routinely zero,
              so it rendered at 0px height — not clipped, not
              scrollable, just gone. Matching the other three fixes
              that: every panel keeps its full natural height, and the
              Stack itself scrolls (`overflowY: 'auto'` above) once
              their combined height exceeds the row. */}
          <Box sx={{ flex: { xs: 'none', md: 1, lg: 'none' }, minHeight: { xs: 220, md: 0 }, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <RequestsByIssueTemplate
              requests={requests}
              templates={templates}
              templatesLoading={templatesQ.isLoading}
              templateId={templateId}
              onTemplateId={setTemplateId}
            />
          </Box>
          <Box sx={{ flex: { xs: 'none', md: 1, lg: 'none' }, minHeight: { xs: 220, md: 0 }, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <ActivityTrend requests={requests} />
          </Box>
          <Box sx={{ flex: { xs: 'none', md: 1, lg: 'none' }, minHeight: { xs: 220, md: 0 }, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <AgingReport requests={requests} />
          </Box>
          <Box sx={{ flex: { xs: 'none', md: 1, lg: 'none' }, minHeight: { xs: 220, md: 0 }, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <TopReporters requests={requests} />
          </Box>
          <Box sx={{ flex: { xs: 'none', md: 1, lg: 'none' }, minHeight: { xs: 220, md: 0 }, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <TopSites requests={requests} sites={sites} />
          </Box>
        </Stack>
      </Box>
    </Stack>
  )
}

// ────────────────────────────────────────────────────────────────
// Header stat tile
// ────────────────────────────────────────────────────────────────

function StatTile({
  label, value, icon: Icon, tone = 'muted',
}: {
  label: string
  value: number
  icon: React.ElementType
  tone?: 'muted' | 'brand' | 'warn' | 'ok'
}) {
  const base =
    tone === 'brand' ? '#4338CA'
    : tone === 'warn'  ? '#B45309'
    : tone === 'ok'    ? '#059669'
    :                    '#475569'

  return (
    <Card sx={{
      flex: 1, minWidth: 0, px: 2, py: 1.5, borderRadius: 2.5,
      border: '1px solid',
      borderColor: (t) => t.palette.mode === 'light' ? `${base}33` : `${base}55`,
      bgcolor:     (t) => t.palette.mode === 'light' ? `${base}10` : `${base}18`,
      boxShadow: 'none',
      display: 'flex', alignItems: 'center', gap: 1.5,
    }}>
      <Box sx={{
        width: 34, height: 34, borderRadius: 1.5, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        bgcolor: (t) => t.palette.mode === 'light' ? '#FFFFFFCC' : `${base}22`,
        border: '1px solid',
        borderColor: (t) => t.palette.mode === 'light' ? `${base}33` : `${base}44`,
      }}>
        <Icon sx={{ fontSize: 18, color: base }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 800, lineHeight: 1.1, color: base }}>
          {value.toLocaleString()}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 500 }}>
          {label}
        </Typography>
      </Box>
    </Card>
  )
}

// ────────────────────────────────────────────────────────────────
// Dashboard card shell (shared by the analytics rail)
// ────────────────────────────────────────────────────────────────

function DashboardCard({
  title, action, accent = '#3B82F6', children,
}: {
  title: string
  action?: React.ReactNode
  accent?: string
  children: React.ReactNode
}) {
  return (
    <Card sx={{
      flex: 1, minWidth: 0, borderRadius: 2.5, border: '1px solid',
      borderColor: 'divider',
      display: 'flex', flexDirection: 'column',
      position: 'relative', overflow: 'hidden',
      boxShadow: (t) => t.palette.mode === 'light'
        ? `0 10px 24px -14px ${accent}55, 0 2px 6px rgba(15,23,42,0.06)`
        : '0 6px 18px -10px rgba(0,0,0,0.6)',
      transition: 'transform 200ms ease, box-shadow 200ms ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: (t) => t.palette.mode === 'light'
          ? `0 16px 32px -18px ${accent}80, 0 4px 10px rgba(15,23,42,0.08)`
          : '0 10px 26px -12px rgba(0,0,0,0.7)',
      },
    }}>
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${accent} 0%, ${accent}00 100%)` }} />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, pt: 1.75, pb: 1 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: 'text.secondary', flex: 1, minWidth: 0 }}>
          {title}
        </Typography>
        {action}
      </Stack>
      <Box sx={{ px: 2, pb: 1.75, flex: 1, minWidth: 0 }}>{children}</Box>
    </Card>
  )
}

// ────────────────────────────────────────────────────────────────
// Requests by Issue Template — pick a template, see its item
// breakdown among matching requests. Closest real equivalent to
// HSE's "Reports by risk category" (a fixed enum + colour map) since
// there's no fixed category catalogue here — items are admin-defined
// per template (Settings → Issue Templates).
// ────────────────────────────────────────────────────────────────

const BAR_COLORS = ['#3B82F6', '#7C3AED', '#059669', '#EA580C', '#DB2777', '#0891B2', '#B45309', '#65A30D']

// Synthetic entry merged into the template picker — id '' matches the
// "no filter" state everything else in this tab already treats as
// "show all requests" (map, stats, rest of the rail).
const ALL_TEMPLATES_OPTION: IssueTemplate = {
  id: '', name: 'All templates', description: null, is_active: true,
  customer_id: null,
  created_by: null, created_by_name: null, created_at: '', updated_at: '',
  item_count: 0, active_item_count: 0, assigned_site_count: 0,
}

function RequestsByIssueTemplate({
  requests, templates, templatesLoading, templateId, onTemplateId,
}: {
  /** Already scoped to `templateId` by the parent (or unfiltered when
   *  templateId is '' / All) — same array driving the map and stats,
   *  so this panel, the map, and the rest of the rail always agree. */
  requests: MaintenanceRequest[]
  templates: IssueTemplate[]
  templatesLoading: boolean
  templateId: string
  onTemplateId: (id: string) => void
}) {
  // Computed straight off `requests` — every row already carries its
  // own item id + label (see requests.service.js's join), so there's
  // no need to separately fetch a template's item catalog just to
  // build this breakdown. That also means "All templates" works for
  // free: it's the exact same aggregation, just over the unfiltered
  // set instead of one template's subset.
  const { rows, total, unclassified } = useMemo(() => {
    const counts = new Map<string, { label: string; count: number }>()
    let unc = 0
    for (const r of requests) {
      if (!r.issue_template_item_id || !r.issue_template_item_label) { unc += 1; continue }
      const existing = counts.get(r.issue_template_item_id)
      if (existing) existing.count += 1
      else counts.set(r.issue_template_item_id, { label: r.issue_template_item_label, count: 1 })
    }
    const tot = requests.length
    const arr = Array.from(counts.entries())
      .map(([id, { label, count }], i) => ({
        key: id,
        label,
        color: BAR_COLORS[i % BAR_COLORS.length]!,
        count,
        pct: tot > 0 ? (count / tot) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count)
    return { rows: arr, total: tot, unclassified: unc }
  }, [requests])

  const topRow = rows[0]

  return (
    <DashboardCard
      title="Requests by issue template"
      accent={topRow?.color ?? '#3B82F6'}
      action={total > 0 && (
        <Chip label={`${total} total`} size="small" sx={{
          height: 20, fontSize: 10.5, fontWeight: 800,
          bgcolor: (topRow?.color ?? '#3B82F6') + '18',
          color: topRow?.color ?? '#3B82F6',
        }} />
      )}
    >
      <SearchableSelect
        value={templateId}
        onChange={onTemplateId}
        options={[ALL_TEMPLATES_OPTION, ...templates]}
        getValue={(t) => t.id}
        getLabel={(t) => t.name}
        placeholder="Select an issue template"
        loading={templatesLoading}
        size="small"
      />
      <Box sx={{ mt: 1.25 }}>
        {total === 0 ? (
          <Typography sx={{ fontSize: 12, color: 'text.disabled', py: 3, textAlign: 'center' }}>
            {templateId ? 'No requests filed against this template yet.' : 'No requests yet.'}
          </Typography>
        ) : rows.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: 'text.disabled', py: 3, textAlign: 'center' }}>
            {unclassified} request{unclassified === 1 ? '' : 's'} without a specific category.
          </Typography>
        ) : (
          // Fixed maxHeight (not height:'100%') — this card sits inside
          // responsive flex containers whose own height isn't reliably
          // bounded at every breakpoint, so a percentage height resolves
          // to nothing and the list just grows instead of scrolling.
          <Box sx={{
            maxHeight: 210, overflowY: 'auto', pr: 0.5,
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'action.hover', borderRadius: 99 },
          }}>
            <Stack spacing={1}>
              {rows.map((r) => (
                <Box key={r.key}>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.4 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: r.color, flexShrink: 0,
                      boxShadow: `0 0 0 3px ${r.color}22` }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 600, flex: 1, minWidth: 0,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.label}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: r.color }}>{r.count}</Typography>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: 'text.disabled', width: 34, textAlign: 'right' }}>
                      {r.pct.toFixed(0)}%
                    </Typography>
                  </Stack>
                  <Box sx={{ height: 3, borderRadius: 99, bgcolor: 'action.hover' }}>
                    <Box sx={{ width: `${r.pct}%`, height: '100%', borderRadius: 99, bgcolor: r.color, transition: 'width 240ms ease' }} />
                  </Box>
                </Box>
              ))}
              {unclassified > 0 && (
                <Typography sx={{ fontSize: 10.5, color: 'text.disabled', fontWeight: 600, pt: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                  + {unclassified} without a specific category
                </Typography>
              )}
            </Stack>
          </Box>
        )}
      </Box>
    </DashboardCard>
  )
}

// ────────────────────────────────────────────────────────────────
// Activity trend — 14-day area chart (unchanged from the HSE
// original; works off created_at, which every request has).
// ────────────────────────────────────────────────────────────────

function ActivityTrend({ requests }: { requests: MaintenanceRequest[] }) {
  const days = 14
  const perDay = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const arr = new Array(days).fill(0)
    for (const r of requests) {
      const d = new Date(r.created_at)
      d.setHours(0, 0, 0, 0)
      const idx = days - 1 - Math.floor((today.getTime() - d.getTime()) / 86_400_000)
      if (idx >= 0 && idx < days) arr[idx]!++
    }
    return arr
  }, [requests])

  const max = Math.max(1, ...perDay)
  const total7d  = perDay.slice(-7).reduce((a, b) => a + b, 0)
  const totalPre = perDay.slice(0, -7).reduce((a, b) => a + b, 0)
  const delta = total7d - totalPre
  const rising = delta > 0
  const falling = delta < 0
  const trendColor = rising ? '#DC2626' : falling ? '#059669' : '#64748B'
  const trendArrow = rising ? '↑' : falling ? '↓' : '→'
  const deltaTxt = delta === 0 ? 'flat' : `${rising ? '+' : ''}${delta}`

  const w = 200, h = 80, padY = 6
  const stepX = w / (days - 1)
  const yFor = (n: number) => padY + (1 - n / max) * (h - padY * 2)
  const points = perDay.map((n, i) => [i * stepX, yFor(n)] as const)
  const linePath = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`).join(' ')
  const areaPath = `${linePath} L${w},${h} L0,${h} Z`
  const [lastX, lastY] = points[points.length - 1]!

  return (
    <DashboardCard
      title="Activity (14 days)"
      accent={trendColor}
      action={
        <Chip label={`${trendArrow} ${deltaTxt} vs prior 7d`} size="small"
          sx={{ height: 20, fontSize: 10.5, fontWeight: 800, bgcolor: trendColor + '18', color: trendColor }} />
      }
    >
      <Stack direction="row" spacing={2} alignItems="stretch">
        <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column',
            justifyContent: 'center', pr: 1.5, borderRight: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 28, fontWeight: 900, letterSpacing: '-0.02em', lineHeight: 1, color: trendColor }}>
            {total7d}
          </Typography>
          <Typography sx={{ fontSize: 10, color: 'text.disabled', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', mt: 0.5 }}>
            last 7 days
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ width: '100%', height: 80 }}>
            <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="100%" preserveAspectRatio="none" style={{ display: 'block' }}>
              <defs>
                <linearGradient id="ticketsActivityFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={trendColor} stopOpacity="0.45" />
                  <stop offset="100%" stopColor={trendColor} stopOpacity="0.02" />
                </linearGradient>
              </defs>
              <path d={areaPath} fill="url(#ticketsActivityFill)" />
              <path d={linePath} fill="none" stroke={trendColor} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
              <circle cx={lastX} cy={lastY} r={3.5} fill="#FFFFFF" stroke={trendColor} strokeWidth={2} />
            </svg>
          </Box>
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
            <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>14d ago</Typography>
            <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>Today</Typography>
          </Stack>
        </Box>
      </Stack>
    </DashboardCard>
  )
}

// ────────────────────────────────────────────────────────────────
// Aging — how long currently-open requests have been sitting, as a
// day-by-day trend line rather than coarse ranges: the x-axis is the
// exact age in days (0, 1, 2, ... up to the oldest open request), not
// bucketed spans like "1-2 weeks" — every open request's exact age
// contributes to exactly one point on the line.
// ────────────────────────────────────────────────────────────────

function AgingReport({ requests }: { requests: MaintenanceRequest[] }) {
  const { days, counts, openCount, avgAgeDays, oldestDays } = useMemo(() => {
    const now = Date.now()
    const ages = requests.filter(isOpen).map((r) => Math.max(0, Math.floor((now - new Date(r.created_at).getTime()) / 86_400_000)))
    const maxAge = ages.length > 0 ? Math.max(...ages) : 0

    // Zero-fill every day from 0..maxAge (not just the ages that
    // actually occur) — a line chart connecting only the days with
    // data would draw a misleading slope across day-gaps that really
    // have zero open requests at that exact age.
    const byAge = new Array<number>(maxAge + 1).fill(0)
    for (const a of ages) byAge[a]!++

    return {
      days: Array.from({ length: maxAge + 1 }, (_, i) => i),
      counts: byAge,
      openCount: ages.length,
      avgAgeDays: ages.length > 0 ? ages.reduce((a, b) => a + b, 0) / ages.length : 0,
      oldestDays: maxAge,
    }
  }, [requests])

  return (
    <DashboardCard
      title="Request aging"
      accent="#DC2626"
      action={openCount > 0 && (
        <Chip label={`avg ${avgAgeDays.toFixed(1)}d`} size="small"
          sx={{ height: 20, fontSize: 10.5, fontWeight: 800, bgcolor: '#DC262618', color: '#DC2626' }} />
      )}
    >
      {openCount === 0 ? (
        <Typography sx={{ fontSize: 12, color: 'text.disabled', py: 3, textAlign: 'center' }}>
          No open requests — nothing aging.
        </Typography>
      ) : (
        <>
          <LineChart
            height={220}
            series={[{
              data: counts, label: 'Open requests', color: '#DC2626',
              area: true, showMark: counts.length <= 40, curve: 'monotoneX',
            }]}
            xAxis={[{ data: days, scaleType: 'linear', label: 'Days open', tickMinStep: 1 }]}
            yAxis={[{ tickMinStep: 1 }]}
            margin={{ left: 36, right: 12, top: 12, bottom: 40 }}
            grid={{ horizontal: true }}
            sx={{
              '& .MuiChartsAxis-line': { stroke: 'transparent' },
              '& .MuiChartsAxis-tick': { stroke: 'transparent' },
              '& .MuiAreaElement-root': { fillOpacity: 0.15 },
            }}
          />
          <Typography sx={{ fontSize: 10.5, color: 'text.disabled', fontWeight: 600, pt: 0.5, textAlign: 'center' }}>
            Oldest open request: {oldestDays} day{oldestDays === 1 ? '' : 's'}
          </Typography>
        </>
      )}
    </DashboardCard>
  )
}

// ────────────────────────────────────────────────────────────────
// Top reporters — who's filing requests and how many each.
// ────────────────────────────────────────────────────────────────

function TopReporters({ requests }: { requests: MaintenanceRequest[] }) {
  const rows = useMemo(() => {
    type Row = { key: string; name: string; email: string | null; count: number; internal: boolean }
    const grouped = new Map<string, Row>()
    for (const r of requests) {
      const internal = !!r.submitted_by_name
      const name = r.submitted_by_name ?? r.requester_name ?? null
      const email = r.submitted_by_email ?? r.requester_email ?? null
      if (!name && !email) continue
      const key = internal ? `u:${r.submitted_by ?? name ?? email}` : `p:${email ?? name}`
      const existing = grouped.get(key)
      if (existing) existing.count += 1
      else grouped.set(key, { key, name: name ?? email ?? 'Unknown reporter', email, count: 1, internal })
    }
    const arr = Array.from(grouped.values())
    const max = Math.max(1, ...arr.map((r) => r.count))
    return arr.sort((a, b) => b.count - a.count).map((r) => ({ ...r, pct: (r.count / max) * 100 }))
  }, [requests])

  return (
    <DashboardCard title="Top reporters" accent="#4338CA">
      {rows.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: 'text.disabled', py: 3, textAlign: 'center' }}>No reporters yet.</Typography>
      ) : (
        <Box sx={{
          maxHeight: 220, overflowY: 'auto', pr: 0.5, scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'action.hover', borderRadius: 99 },
        }}>
          <Stack spacing={1.5}>
            {rows.map((r, i) => (
              <Box key={r.key}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Box sx={{ width: 22, height: 22, borderRadius: 99, bgcolor: 'action.hover', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 800, color: 'text.secondary' }}>{i + 1}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, minWidth: 0,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.name}
                      </Typography>
                      {!r.internal && (
                        <Chip label="public" size="small" sx={{ height: 14, fontSize: 8.5, fontWeight: 700,
                          bgcolor: '#F59E0B18', color: '#B45309', '& .MuiChip-label': { px: 0.6 } }} />
                      )}
                    </Stack>
                    {r.email && r.email !== r.name && (
                      <Typography sx={{ fontSize: 10.5, color: 'text.disabled',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.email}
                      </Typography>
                    )}
                  </Box>
                  <Chip label={`${r.count} ${r.count === 1 ? 'request' : 'requests'}`} size="small"
                    sx={{ height: 20, fontSize: 10.5, fontWeight: 800, bgcolor: '#4338CA18', color: '#4338CA' }} />
                </Stack>
                <Box sx={{ height: 4, borderRadius: 99, bgcolor: 'action.hover', ml: 3.75 }}>
                  <Box sx={{ width: `${r.pct}%`, height: '100%', borderRadius: 99,
                    background: 'linear-gradient(90deg, #4338CA 0%, #818CF8 100%)', transition: 'width 240ms ease' }} />
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </DashboardCard>
  )
}

// ────────────────────────────────────────────────────────────────
// Top sites — most requests filed, ordered desc.
// ────────────────────────────────────────────────────────────────

function TopSites({ requests, sites }: { requests: MaintenanceRequest[]; sites: Site[] }) {
  const { rows, totalSitesWithRequests } = useMemo(() => {
    const bySite = new Map<string, number>()
    for (const r of requests) {
      if (!r.site_id) continue
      bySite.set(r.site_id, (bySite.get(r.site_id) ?? 0) + 1)
    }
    const nameById = new Map(sites.map((s) => [s.id, s.name]))
    const addrById = new Map(sites.map((s) => [s.id, s.address]))
    const max = Math.max(1, ...bySite.values())
    const all = Array.from(bySite.entries())
      .map(([id, count]) => ({
        id, count, pct: (count / max) * 100,
        name: nameById.get(id) ?? 'Unknown site',
        address: addrById.get(id) ?? null,
      }))
      .sort((a, b) => b.count - a.count)
    return { rows: all, totalSitesWithRequests: all.length }
  }, [requests, sites])

  const VISIBLE_HINT = 4

  return (
    <DashboardCard title="Sites needing attention" accent="#B45309">
      {rows.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: 'text.disabled', py: 3, textAlign: 'center' }}>No site requests yet.</Typography>
      ) : (
        <Box sx={{
          maxHeight: 260, overflowY: 'auto', pr: 0.5, scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'action.hover', borderRadius: 99 },
        }}>
          <Stack spacing={1.5}>
            {rows.map((r, i) => (
              <Box key={r.id}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Box sx={{ width: 22, height: 22, borderRadius: 99, bgcolor: 'action.hover', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 800, color: 'text.secondary' }}>{i + 1}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, minWidth: 0,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.name}
                    </Typography>
                    {r.address && (
                      <Typography sx={{ fontSize: 10.5, color: 'text.disabled',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.address}
                      </Typography>
                    )}
                  </Box>
                  <Chip label={`${r.count} ${r.count === 1 ? 'request' : 'requests'}`} size="small"
                    sx={{ height: 20, fontSize: 10.5, fontWeight: 800, bgcolor: '#B4530918', color: '#B45309' }} />
                </Stack>
                <Box sx={{ height: 4, borderRadius: 99, bgcolor: 'action.hover', ml: 3.75 }}>
                  <Box sx={{ width: `${r.pct}%`, height: '100%', borderRadius: 99,
                    background: 'linear-gradient(90deg, #B45309 0%, #F59E0B 100%)', transition: 'width 240ms ease' }} />
                </Box>
              </Box>
            ))}
          </Stack>
          {totalSitesWithRequests > VISIBLE_HINT && (
            <Typography sx={{ fontSize: 10.5, color: 'text.disabled', textAlign: 'center', mt: 1.25, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
              Scroll for {totalSitesWithRequests - VISIBLE_HINT} more site{totalSitesWithRequests - VISIBLE_HINT === 1 ? '' : 's'}
            </Typography>
          )}
        </Box>
      )}
    </DashboardCard>
  )
}

// ────────────────────────────────────────────────────────────────
// Map + click-through detail card
// ────────────────────────────────────────────────────────────────

interface PinSite {
  site: Site
  requests: MaintenanceRequest[]
}

function pinIcon(count: number): L.DivIcon {
  const width = count >= 100 ? 42 : count >= 10 ? 38 : 34
  return L.divIcon({
    className: 'cmms-ticket-pin',
    html: `
      <div style="width: ${width}px; height: 34px; border-radius: 999px; background: #FFFFFF; padding: 3px; box-sizing: border-box;
        box-shadow: 0 4px 10px rgba(15,23,42,0.25), 0 1px 3px rgba(15,23,42,0.15);">
        <div style="width: 100%; height: 100%; border-radius: 999px; background: #DC2626; display: flex; align-items: center;
          justify-content: center; color: #FFFFFF; font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
          font-size: 13px; font-weight: 800; letter-spacing: -0.2px;">${count}</div>
      </div>
    `,
    iconSize: [width, 34],
    iconAnchor: [width / 2, 17],
  })
}

function SiteRequestsMap({ pinSites }: { pinSites: PinSite[] }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef       = useRef<L.Map | null>(null)
  const tileLayerRef = useRef<L.LayerGroup | null>(null)
  // Keyed by site id (was a plain array) — lets the sync effect below
  // diff add/remove/update instead of tearing down and recreating
  // every marker on every run. Mirrors locations/components/MapView.tsx's
  // already-optimized marker sync.
  const markersRef   = useRef<Map<string, L.Marker>>(new Map())
  const [selected, setSelected] = useState<PinSite | null>(null)
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return
    const map = L.map(containerRef.current, { center: [20, 0], zoom: 2, zoomControl: false, attributionControl: false })
    L.control.zoom({ position: 'topleft' }).addTo(map)
    mapRef.current = map
    return () => { map.remove(); mapRef.current = null; tileLayerRef.current = null; markersRef.current.clear() }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (tileLayerRef.current) { tileLayerRef.current.remove(); tileLayerRef.current = null }
    // Esri's free tile services need no API key, unlike CARTO's CDN
    // (which now watermarks unauthenticated tiles). Street Map is
    // colourful and self-labelled; the dark canvas has no baked-in
    // labels so it needs a separate reference layer stacked on top.
    const layers = isDark
      ? [
          L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}', { maxZoom: 20, maxNativeZoom: 16 }),
          L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}', { maxZoom: 20, maxNativeZoom: 16 }),
        ]
      : [L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}', { maxZoom: 20, maxNativeZoom: 19 })]
    tileLayerRef.current = L.layerGroup(layers).addTo(map)
  }, [isDark])

  // Deselect if the currently-open pin dropped out of the filtered set
  // — its own effect so switching filters doesn't touch the markers.
  useEffect(() => {
    if (selected && !pinSites.find((p) => p.site.id === selected.site.id)) setSelected(null)
  }, [pinSites, selected])

  // Sync markers — diffs against the existing id→marker map instead of
  // tearing every marker down and rebuilding from scratch. That old
  // approach also depended on `selected`, so clicking any single pin
  // (which sets `selected`) re-ran the whole rebuild for every marker
  // on the map — the actual slowdown as pin count grew, not just the
  // per-render cost. Deliberately NOT depending on `selected` here.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const existing = markersRef.current
    const wantedIds = new Set(pinSites.map((p) => p.site.id))

    for (const [id, marker] of existing) {
      if (!wantedIds.has(id)) {
        marker.remove()
        existing.delete(id)
      }
    }

    for (const p of pinSites) {
      const icon = pinIcon(p.requests.length)
      const tooltip = `<strong>${escapeHtml(p.site.name)}</strong><br />${p.requests.length} request${p.requests.length === 1 ? '' : 's'}`
      let marker = existing.get(p.site.id)
      if (!marker) {
        marker = L.marker([p.site.latitude as number, p.site.longitude as number], { icon })
          .on('click', () => setSelected(p))
          .bindTooltip(tooltip, { direction: 'top', offset: [0, -12] })
          .addTo(map)
        existing.set(p.site.id, marker)
      } else {
        marker.setIcon(icon)
        marker.setTooltipContent(tooltip)
      }
    }
  }, [pinSites])

  // Fit bounds — guarded by a stable signature of the pin set so this
  // only re-runs when which sites are shown actually changes, never on
  // a pin click (selection lives in separate state, not a dependency
  // here) or an unrelated re-render.
  const fittedForRef = useRef('')
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const sig = pinSites.map((p) => p.site.id).sort().join(',')
    if (sig === fittedForRef.current) return
    fittedForRef.current = sig
    const bounds = pinSites.map((p) => [p.site.latitude as number, p.site.longitude as number] as L.LatLngExpression)
    if (bounds.length === 1) map.setView(bounds[0]!, 12)
    else if (bounds.length > 1) map.fitBounds(bounds as L.LatLngBoundsLiteral, { padding: [40, 40], maxZoom: 12 })
  }, [pinSites])

  return (
    <>
      <Box ref={containerRef} sx={{ position: 'absolute', inset: 0, zIndex: 0 }} />
      {pinSites.length === 0 && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <Card sx={{ px: 3, py: 2, borderRadius: 2, textAlign: 'center', maxWidth: 320,
              boxShadow: (t) => t.palette.mode === 'light' ? '0 8px 24px -6px rgba(15,23,42,0.14)' : '0 8px 24px -6px rgba(0,0,0,0.4)' }}>
            <LocationOnOutlinedIcon sx={{ fontSize: 26, color: 'text.disabled', mb: 0.5 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.25 }}>No sites match this filter</Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Try switching the filter above.</Typography>
          </Card>
        </Box>
      )}
      {selected && <SiteRequestsCard pin={selected} onClose={() => setSelected(null)} />}
    </>
  )
}

const STATUS_META: Record<MaintenanceRequest['status'], { label: string; bg: string; color: string }> = {
  submitted: { label: 'Submitted', bg: '#FEF3C7', color: '#92400E' },
  approved:  { label: 'Approved',  bg: '#DBEAFE', color: '#1D4ED8' },
  declined:  { label: 'Declined', bg: '#FEE2E2', color: '#991B1B' },
  closed:    { label: 'Closed',   bg: '#DCFCE7', color: '#166534' },
}

function SiteRequestsCard({ pin, onClose }: { pin: PinSite; onClose: () => void }) {
  const { formatDate } = useOrgSettings()
  const woCount   = pin.requests.filter((r) => r.work_order_kind === 'work_order').length
  const taskCount = pin.requests.filter((r) => r.work_order_kind === 'task').length
  const withWo = woCount + taskCount

  return (
    <Card sx={{
      position: 'absolute', top: 16, right: 16, width: 360, maxHeight: 'calc(100% - 32px)',
      overflowY: 'auto', zIndex: 1000, borderRadius: 2.5,
      boxShadow: (t) => t.palette.mode === 'light' ? '0 16px 40px -12px rgba(15,23,42,0.28)' : '0 16px 40px -12px rgba(0,0,0,0.6)',
    }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <LocationOnOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {pin.site.name}
          </Typography>
          {pin.site.address && (
            <Typography sx={{ fontSize: 11, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {pin.site.address}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ mr: -0.5 }}><CloseIcon fontSize="small" /></IconButton>
      </Stack>

      <Box sx={{ px: 2, py: 1.25, bgcolor: 'action.hover' }}>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip icon={<AssignmentTurnedInOutlinedIcon sx={{ fontSize: 14 }} />}
            label={`${withWo} of ${pin.requests.length} converted`}
            size="small"
            sx={{ fontSize: 11, fontWeight: 700, bgcolor: withWo === pin.requests.length && pin.requests.length > 0 ? '#DCFCE7' : '#FEF3C7',
              color: withWo === pin.requests.length && pin.requests.length > 0 ? '#166534' : '#92400E' }} />
          {woCount > 0 && (
            <Chip label={`${woCount} work order${woCount === 1 ? '' : 's'}`} size="small"
              sx={{ fontSize: 11, fontWeight: 700, bgcolor: '#EFF6FF', color: '#1D4ED8' }} />
          )}
          {taskCount > 0 && (
            <Chip label={`${taskCount} task${taskCount === 1 ? '' : 's'}`} size="small"
              sx={{ fontSize: 11, fontWeight: 700, bgcolor: '#F5F3FF', color: '#6D28D9' }} />
          )}
        </Stack>
      </Box>

      <Stack divider={<Divider />} spacing={0}>
        {pin.requests.map((r) => (
          <Box key={r.id} sx={{ px: 2, py: 1.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.25, overflow: 'hidden', textOverflow: 'ellipsis',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
              {r.title}
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{formatDate(r.created_at)}</Typography>
              {(r.submitted_by_name ?? r.requester_name) && (
                <>
                  <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>·</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <PeopleAltOutlinedIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{r.submitted_by_name ?? r.requester_name}</Typography>
                  </Stack>
                </>
              )}
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip label={STATUS_META[r.status].label} size="small"
                sx={{ fontSize: 11, fontWeight: 700, height: 22, bgcolor: STATUS_META[r.status].bg, color: STATUS_META[r.status].color }} />
              {r.work_order_kind === 'work_order' && (
                <Chip icon={<BuildOutlinedIcon sx={{ fontSize: 13 }} />} label="Work order" size="small"
                  sx={{ fontSize: 11, fontWeight: 700, height: 22, bgcolor: '#EFF6FF', color: '#1D4ED8' }} />
              )}
              {r.work_order_kind === 'task' && (
                <Chip icon={<AssignmentTurnedInOutlinedIcon sx={{ fontSize: 13 }} />} label="Task" size="small"
                  sx={{ fontSize: 11, fontWeight: 700, height: 22, bgcolor: '#F5F3FF', color: '#6D28D9' }} />
              )}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Card>
  )
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}
